'use strict';


import { EnvironmentLayer } from './EnvironmentLayer';
import { Loader } from '../../../threejs/loader/Loader';
import { projectMode, resourcesFolder, threeJsVariables } from '../../../../settings';
import { Cloudflare } from '../../../api/Cloudflare';
import { getMaterialType, prepareMaterial } from '../../../threejs/autoProjectScript';
import { BatchedMesh, Euler, InstancedMesh, Matrix4, Quaternion, Vector3 } from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { InstanceLOD } from '../../../threejs/InstanceLOD/InstanceLOD';

export class BuildingsEnvironment extends EnvironmentLayer{
	constructor () {
		super('Buildings');
		
		this.types = {};
		this.batch = {
			globalRoof: []
		};
		this.instances = {};
		this.materials = [];
		// this.useBundle = config.features.environment?.useBundle;
		this.globalRoofMaterial = null;
	}
	
	// load() {
	// 	// if (!this.useBundle) return;
	// 	//
	// 	// const loader = new Loader();
	// 	// const dracoLoader = loader.dracoLoader;
	// 	// const textureLoader = loader.textureLoader;
	// 	// const objectLoader = loader.objectLoader;
	// 	//
	// 	// dracoLoader.load(`${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/points/osm.glb?v=${Cloudflare.version}`,glb => {
	// 	// 	const scene = glb.scene;
	// 	// 	const config = glb.scene.userData.config;
	// 	//
	// 	// 	this.#createGlobalRoofMaterial(config,textureLoader);
	// 	// 	this.#createTypes(config,textureLoader,objectLoader);
	// 	// 	this.#prepareBatchData(config,scene);
	// 	// 	this.#createBuildings();
	// 	// })
	// }
	
	loadFromBundle (scene, conf) {
		const config = conf[this.id];
		if (!config || projectMode.AirMode.active) return;
		const loader = new Loader();
		const textureLoader = loader.textureLoader;
		const objectLoader = loader.objectLoader;
		
		this.settings = config.settings;
		this.#createGlobalRoofMaterial(config,textureLoader);
		this.#createTypes(config,textureLoader,objectLoader);
		this.#prepareInstanceObjects(config,scene);
		this.#prepareBatchData(config,scene);
		this.#createBuildings();
	}
	
	#createGlobalRoofMaterial(config,textureLoader) {
		this.globalRoofMaterial = prepareMaterial(config.globalRoof.config,textureLoader,config.globalRoof.type);
	}
	
	#createTypes(config,textureLoader,objectLoader) {
		Object.entries(config.types).forEach(([name,data]) => {
			this.types[name] = {};
			
			if (data.isStoreObject) {
				const materialType = getMaterialType(threeJsVariables.bundleConfig,`${name}_osm`);
				this.types[name].material = prepareMaterial(data.config.material,textureLoader,materialType);
				
				if (Array.isArray(this.types[name].material)) {
					this.types[name].material.forEach((material,index) => {
						if (material.emissiveMap && !data.config.material[index].emissiveMap.startsWith('nightLights')) {
							this.materials.push(material);
						}
					})
				} else if (this.types[name].material.emissiveMap && !data.config.material.emissiveMap.startsWith('nightLights')) {
					this.materials.push(this.types[name].material);
				}
				
				this.types[name].config = data.config;
				this.types[name].isStoreObject = true;
			} else if (data.geometry) {
				this.batch[name] = [];
				this.types[name].geometry = mergeVertices(Object.values(objectLoader.parseGeometries([data.geometry]))[0]);
				this.types[name].material = prepareMaterial(data.material.config,textureLoader,data.material.type);
				
				if (this.types[name].material.emissiveMap) {
					this.materials.push(this.types[name].material);
				}
			} else {
				this.batch[name] = [];
				if (data.roofMaterial) {
					this.types[name].roofMaterial = prepareMaterial(data.roofMaterial.config,textureLoader,data.roofMaterial.type);
				}
				
				if (data.sidewallMaterial) {
					this.types[name].sidewallMaterial = prepareMaterial(data.sidewallMaterial.config,textureLoader,data.sidewallMaterial.type);
					
					if (this.types[name].sidewallMaterial.emissiveMap) {
						this.materials.push(this.types[name].sidewallMaterial);
					}
				}
			}
		});
	}
	
	setAlpha(alpha) {
		this.materials.forEach(material => {
			material.emissiveIntensity = 1 - alpha;
		});
	}
	
	#prepareInstanceObjects(config,scene) {
		Object.entries(this.types).forEach(([name,data]) => {
			if (!data.isStoreObject)
				return;
			
			const object = scene.getObjectByName(name);
			if (object) {
				const points = data.config.points;
				const isLOD = data.config.isLOD;
				const distances = data.config.distances;
				const count = points.length;
				
				if (isLOD) {
					const instanceLOD = new InstanceLOD();
					instanceLOD.name = name;
					
					object.children.forEach((lod,index) => {
						if (lod.userData.groups) {
							lod.geometry.groups = lod.userData.groups;
						}
						lod.material = data.material[index];
						
						const instance = new InstancedMesh(lod.geometry,lod.material,count);
						instance.name = lod.name;
						
						points.forEach((point,index) => {
							const matrix = new Matrix4();
							matrix.fromArray(point.matrix);
							instance.setMatrixAt(index, matrix);
						});
						
						instance.computeBoundingSphere();
						instanceLOD.addLevel(instance,distances[index]);
					});
					
					this.group.add(instanceLOD);
					
				} else {
					const instance = new InstancedMesh(object.geometry,data.material,count);
					instance.name = object.name;
					
					points.forEach((point,index) => {
						const matrix = new Matrix4();
						matrix.fromArray(point.matrix);
						instance.setMatrixAt(index, matrix);
					});
					instance.computeBoundingSphere();
					this.group.add(instance);
				}
			}
		});
	}
	
	#prepareBatchData(config,scene) {
		const osm = config.osm;
		const position = new Vector3();
		const quaternion = new Quaternion();
		const scale = new Vector3();
		
		if (osm) {
			if (Object.keys(this.types).length === 0) {
				this.batch.default = {
					sidewall: []
				};
				
				osm.forEach(building => {
					const id = building.data.id.toString();
					const object = scene.getObjectByName(id);
					position.copy(object.position);
					quaternion.copy(object.quaternion);
					scale.copy(object.scale);
					
					this.batch.default.sidewall.push({
						geometry: object.getObjectByName(id + '_sidewall').geometry,
						material: this.globalRoofMaterial,
						matrix: new Matrix4().compose(position,quaternion,scale)
					});
					
					this.batch.globalRoof.push({
						geometry: object.getObjectByName(id + '_roof').geometry,
						material: this.globalRoofMaterial,
						matrix: new Matrix4().compose(position,quaternion,scale)
					})
					
				});
				return;
			}
			
			Object.entries(this.types).forEach(([name,data]) => {
				if (data.isStoreObject) {
					return;
				} else if (data.geometry) {
					this.batch[name] = [];
				} else {
					this.batch[name] = {
						sidewall: []
					};
				}
				
				const buildings = osm.filter(building => building.type === name);
				buildings.forEach(building => {
					const id = building.data.id.toString();
					const object = scene.getObjectByName(id);
					position.copy(object.position);
					quaternion.copy(object.quaternion);
					scale.copy(object.scale);
					
					if (data.geometry) {
						this.batch[name].push({
							geometry: data.geometry,
							material: data.material,
							matrix: new Matrix4().compose(position,quaternion,scale)
						})
					} else {
						
						if (data.roofMaterial) {
							if (!this.batch[name].roof) {
								this.batch[name].roof = [];
							}
							
							this.batch[name].roof.push({
								geometry: object.getObjectByName(id + '_roof').geometry,
								material: data.roofMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						} else {
							this.batch.globalRoof.push({
								geometry: object.getObjectByName(id + '_roof').geometry,
								material: this.globalRoofMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						}
						
						if (data.sidewallMaterial) {
							this.batch[name].sidewall.push({
								geometry: object.getObjectByName(id + '_sidewall').geometry,
								material: data.sidewallMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						}
					}
				})
			});
			
		} else {
			const buildingsGroup = scene.getObjectByName('Buildings');
			
			if (Object.keys(this.types).length === 0) {
				this.batch.default = {
					sidewall: []
				};
				
				buildingsGroup.children.forEach(object => {
					const id = object.name;
					position.copy(object.position);
					quaternion.copy(object.quaternion);
					scale.copy(object.scale);
					
					this.batch.default.sidewall.push({
						geometry: object.getObjectByName(id + '_sidewall').geometry,
						material: this.globalRoofMaterial,
						matrix: new Matrix4().compose(position,quaternion,scale)
					});
					
					this.batch.globalRoof.push({
						geometry: object.getObjectByName(id + '_roof').geometry,
						material: this.globalRoofMaterial,
						matrix: new Matrix4().compose(position,quaternion,scale)
					});
				});
				return;
			}
			
			Object.entries(this.types).forEach(([name,data]) => {
				
				if (data.isStoreObject) {
					return;
				} else if (data.geometry) {
					this.batch[name] = [];
				} else {
					this.batch[name] = {
						sidewall: []
					};
				}
				
				const buildings = buildingsGroup.children.filter(building => building.userData.type === name);
				buildings.forEach(object => {
					const id = object.name;
					position.copy(object.position);
					quaternion.copy(object.quaternion);
					scale.copy(object.scale);
					
					if (data.geometry) {
						this.batch[name].push({
							geometry: data.geometry,
							material: data.material,
							matrix: new Matrix4().compose(position,quaternion,scale)
						})
					} else {
						
						if (data.roofMaterial) {
							if (!this.batch[name].roof) {
								this.batch[name].roof = [];
							}
							
							this.batch[name].roof.push({
								geometry: object.getObjectByName(id + '_roof').geometry,
								material: data.roofMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						} else {
							this.batch.globalRoof.push({
								geometry: object.getObjectByName(id + '_roof').geometry,
								material: this.globalRoofMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						}
						
						if (data.sidewallMaterial) {
							this.batch[name].sidewall.push({
								geometry: object.getObjectByName(id + '_sidewall').geometry,
								material: data.sidewallMaterial,
								matrix: new Matrix4().compose(position,quaternion,scale)
							})
						}
					}
				})
			});
		}
	}
	
	#createBuildings() {
		const create = (name,array) => {
			if (!array.length) return;

			array = array.filter(item => item.geometry)
			const count = array.length;
			const vertexCount = array.reduce((acc,v) => acc+= v.geometry.attributes.position.count,0);
			const indexCount = array.reduce((acc,v) => acc+= v.geometry.index.count,0);
			
			const mesh = new BatchedMesh(count,vertexCount,indexCount,array[0].material);
			array.forEach(data => {
				const id = mesh.addGeometry(data.geometry);
				mesh.setMatrixAt(id,data.matrix);
			});
			mesh.name = name;
			this.group.add(mesh);
		};
		
		Object.entries(this.batch).forEach(([name,data]) => {
			if (Array.isArray(data)) {
				create(name,data);
			} else {
				Object.entries(data).forEach(([subName,subData]) => {
					create(name + '_'+ subName,subData)
				});
			}
		});
	}
	
	show () {
		this.group.visible = true;
	}
	
	hide () {
		this.group.visible = false;
	}
	
	set settings(data) {
		if (data) {
			this.group.position.fromArray(data.position);
		}
	}
	
}
